import { AfterViewInit, ChangeDetectionStrategy, Component, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { PickList } from 'primeng/picklist';

import { GridActions, GridService, NotificationService, WindowService } from '@core/services';
import { remToPx } from '@util/layout';

import { Column, Columns } from '#types/grid';

import { NoResultsComponent } from '../no-results/no-results.component';

@Component({
  selector: 'z-grid-columns',
  templateUrl: './columns.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnsComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  display$ = new BehaviorSubject<boolean>(true);

  @Input() gridId: string;

  picklistHeight$ = new BehaviorSubject<any>(null);
  availableColumns$ = new BehaviorSubject<Columns>(null);
  selectedColumns$ = new BehaviorSubject<Columns>(null);

  @ViewChild('pickList') pickList: PickList;

  @ViewChild('noColumnsSource') noColumnsSource: NoResultsComponent;
  @ViewChild('noColumnsTarget') noColumnsTarget: NoResultsComponent;
  @ViewChild('noResultsSource') noResultsSource: NoResultsComponent;
  @ViewChild('noResultsTarget') noResultsTarget: NoResultsComponent;

  container: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private store: Store,
    public gridService: GridService,
    private translocoService: TranslocoService,
    private notificationService: NotificationService,
    private ngZone: NgZone,
    public windowService: WindowService
  ) {}

  ngOnInit() {
    this.gridService
      .selectedColumns$(this.gridId)
      .pipe(
        withLatestFrom(this.gridService.availableColumns$(this.gridId)),
        filter(([selectedColumns, availableColumns]) => !!selectedColumns && !!availableColumns),
        takeUntil(this.destroy$)
      )
      .subscribe(([selectedColumns, availableColumns]) => {
        this.selectedColumns$.next([...selectedColumns]);

        const available: Columns = [];

        for (const column of [...availableColumns].sort((a, b) => this.sortByColumnHeader(a, b))) {
          if (!column.canToggleVisibility) {
            continue;
          }

          available.push(column);
        }

        this.availableColumns$.next([...available]);
      });

    this.gridService.columnSettingsPanel$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value === 'open') {
        this.display$.next(true);

        this.calculatePanelSize();
      } else {
        this.display$.next(false);
      }
    });

    this.windowService.mobile$.pipe(takeUntil(this.destroy$)).subscribe((mobile) => {
      if (mobile) {
        this.picklistHeight$.next({ height: '30vh' });
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  sortByColumnHeader(a: Column, b: Column) {
    if (!a.header) {
      return 1;
    }

    if (!b.header) {
      return -1;
    }

    return a.header.localeCompare(b.header);
  }

  trackBy(_index: number, item: Column) {
    return item.key;
  }

  onVisibleChange(event: any) {
    if (event === false) {
      this.close();
    }
  }

  apply() {
    this.selectedColumns$.pipe(withLatestFrom(this.availableColumns$), take(1)).subscribe(([selectedColumns, availableColumns]) => {
      if (selectedColumns.length === 0) {
        this.notificationService.error({
          title: this.translate('core.components.grid.columns.error_message.title'),
          message: this.translate('core.components.grid.columns.error_message.detail'),
        });

        return;
      }

      this.store.dispatch(GridActions.SetColumns({ id: this.gridId, selectedColumns, availableColumns }));
      this.close();
    });
  }

  reset() {
    this.gridService
      .selectedColumns$(this.gridId)
      .pipe(
        withLatestFrom(
          this.gridService.availableColumns$(this.gridId),
          this.gridService.defaultSelectedColumns$(this.gridId),
          this.gridService.defaultAvailableColumns$(this.gridId)
        ),
        take(1)
      )
      .subscribe(([selectedColumns, availableColumns, defaultSelectedColumns, defaultAvailableColumns]) => {
        const available: Columns = [];
        const selected: Columns = [];

        for (const defaultColumn of defaultSelectedColumns) {
          for (const column of [...selectedColumns, ...availableColumns]) {
            if (defaultColumn.field === column.field && defaultColumn.visible) {
              selected.push(column);
              continue;
            }
          }
        }

        for (const defaultColumn of defaultAvailableColumns) {
          for (const column of [...selectedColumns, ...availableColumns]) {
            if (defaultColumn.field === column.field && defaultColumn.visible) {
              available.push(column);
              continue;
            }
          }
        }

        this.availableColumns$.next([...available].sort((a, b) => this.sortByColumnHeader(a, b)));
        this.selectedColumns$.next([...selected]);
      });
  }

  close() {
    this.store.dispatch(GridActions.closeColumnPanel());
    this.pickList.resetFilter();
  }

  calculatePanelSize() {
    setTimeout(() => {
      if (!this.container) {
        this.initResizeObserver();
      }

      const header: HTMLDivElement = this.container.querySelector('.p-dialog-header');
      const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
      const picklistFilter: HTMLDivElement = this.container.querySelector('.p-picklist-filter-container');
      const picklistHeader: HTMLDivElement = this.container.querySelector('.p-picklist-header');

      let offset = 0;

      if (header) {
        offset += Math.floor(header.offsetHeight);
      }

      if (footer) {
        offset += Math.floor(footer.offsetHeight);
      }

      if (picklistFilter) {
        offset += Math.floor(picklistFilter.offsetHeight);
      }

      if (picklistHeader) {
        offset += Math.floor(picklistHeader.offsetHeight);
      }

      offset += remToPx('4rem');

      this.picklistHeight$.next({ height: `${Math.floor(this.container.offsetHeight) - offset}px` });

      if (this.noColumnsSource) {
        this.noColumnsSource.update();
      }

      if (this.noColumnsTarget) {
        this.noColumnsTarget.update();
      }

      if (this.noResultsSource) {
        this.noResultsSource.update();
      }

      if (this.noResultsTarget) {
        this.noResultsTarget.update();
      }
    }, 0);
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        this.calculatePanelSize();
      });
    });

    try {
      this.container = document.querySelector(`.grid-columns-dialog`);

      if (this.container) {
        this.containerResizeObserver.observe(this.container);
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.container) {
        this.containerResizeObserver.unobserve(this.container);
      }
    } catch {}
  }
}
